/**
 * Utility functions for managing form data persistence in sessionStorage
 * This allows form data to persist across tab switches but not browser closes
 */

import { FORM } from 'appConfig/enums'

// Generate storage key for a form
export const getFormStorageKey = (formName: string): string => {
  return `form_${formName}`
}

// Safely get data from sessionStorage
export const getPersistedFormData = (formName: string): any | null => {
  try {
    const key = getFormStorageKey(formName)
    const item = sessionStorage.getItem(key)
    return item ? JSON.parse(item) : null
  } catch (error) {
    console.warn(`Failed to read persisted form data for ${formName}:`, error)
    return null
  }
}

// Safely save data to sessionStorage
export const persistFormData = (formName: string, data: any): void => {
  try {
    const key = getFormStorageKey(formName)
    sessionStorage.setItem(key, JSON.stringify(data))
  } catch (error) {
    console.warn(`Failed to persist form data for ${formName}:`, error)
  }
}

// Safely remove data from sessionStorage
export const clearPersistedFormData = (formName: string): void => {
  try {
    const key = getFormStorageKey(formName)
    sessionStorage.removeItem(key)
  } catch (error) {
    console.warn(`Failed to clear persisted form data for ${formName}:`, error)
  }
}

// Clear persisted data for the user task form specifically
export const clearUserTaskFormData = (): void => {
  clearPersistedFormData(FORM.USER_TASK_FORM)
}

// Helper to wrap modal close handlers with form data cleanup
export const withFormCleanup = (
  closeHandler: () => void,
  formName: string = FORM.USER_TASK_FORM
): (() => void) => {
  return () => {
    clearPersistedFormData(formName)
    closeHandler()
  }
}
